import { useCallback, useEffect, useState } from 'react';

import { useCodeRunner } from 'dashboard/components/CodeRunner/CodeRunner';
import useDebounced from 'lib/hooks/useDebounced';
import JavascriptTemplatedScript from 'lib/JavascriptTemplatedScript';

/**
 * @params code - the code to be evaluated, e.g {{ query1.result.toLowerCase() }}
 * @params globals - the globals to be passed into the code as context, e.g { query1: { result: 'Some results' }}}
 * @params fallback - the fallback value to be used if the code fails to evaluate
 *
 *
 * @returns [isLoading, value, error] - the loading state of the resolver, the value of the evaluated code (e.g 'some results' from the example above), and the error if any
 *
 * */
export function useJavascriptResolver(
  code: string,
  globals: Record<string, unknown>,
  fallback?: string | null,
): [boolean, string | null, Error | null] {
  const { runCode } = useCodeRunner();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [value, setValue] = useState<string | null>(fallback);
  const [error, setError] = useState<Error | null>(null);

  const callback = useCallback(() => {
    if (!code) {
      setIsLoading(false);
      return;
    }

    // translate code from handlebar syntax to javascript templated
    // e.g { "userId": "{{ case.inquiry.reference_id }}" } => { "userId": "{{0}}" }
    const templated = new JavascriptTemplatedScript(code);

    Promise.all(templated.javascripts.map((script) => runCode(`return ${script};`, globals)))
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      .then((resolvedValues: (string | number)[]) => {
        // replace the templated values with the computed values, e.g find {{index}} and replace with the computed value
        const resolvedResult = templated.evaluate(resolvedValues);
        setValue(resolvedResult);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setError(error);
        setIsLoading(false);
      });
  }, [code, globals, runCode]);

  const [debouncedCallback] = useDebounced(callback, 500, {
    leading: true,
    trailing: true,
  });

  useEffect(() => {
    if (!code) {
      setIsLoading(false);
      return;
    }

    debouncedCallback();
  }, [code, debouncedCallback]);

  return [isLoading, value, error];
}
